import React from 'react';
import {
  Box,
  alpha,
  useTheme,
} from '@mui/material';
import { CaptionText } from '../typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FormSelect from '../forms/FormSelect';
import type { DropdownOption } from '../forms/FormSelect';
import { borderRadius, layoutGap } from '../../../theme/theme';

export interface CompactFieldRowProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  help?: React.ReactNode;
  error?: string;
  required?: boolean;
  validationFocused?: boolean;
  allowCustomValue?: boolean;
  disabled?: boolean;
}

export const CompactFieldRow: React.FC<CompactFieldRowProps> = ({
  label,
  value,
  onChange,
  options,
  help,
  error,
  required = false,
  validationFocused = false,
  allowCustomValue = true,
  disabled = false,
}) => {
  const theme = useTheme();
  const hasValue = value.trim().length > 0;
  const isError = !!error;

  return (
    <Box
      data-validation-focus={validationFocused ? 'true' : undefined}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: layoutGap.dense,
        p: 0.1,
        minWidth: 0,
        borderRadius: `${borderRadius.sm}px`,
        backgroundColor: validationFocused
          ? alpha(theme.palette.warning.main, 0.08)
          : isError
            ? alpha(theme.palette.error.main, 0.04)
            : 'transparent',
        border: `1px solid ${
          validationFocused
            ? alpha(theme.palette.warning.main, 0.28)
            : isError
              ? alpha(theme.palette.error.main, 0.28)
              : 'transparent'
        }`,
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <FormSelect
          label={label.replace(' *', '')}
          value={value}
          onChange={onChange}
          options={options}
          help={help}
          error={error}
          dense
          allowCustomValue={allowCustomValue}
          disabled={disabled}
          labelAccessory={
            hasValue ? (
              <CheckCircleIcon sx={{ fontSize: 13, color: theme.palette.success.main }} />
            ) : required ? (
              <CaptionText sx={{ color: theme.palette.warning.main }}>
                Required
              </CaptionText>
            ) : null
          }
        />
      </Box>
    </Box>
  );
};

export default CompactFieldRow;
